import { Button } from '@/components/ui/button';
import { 
  CheckCircle, 
  Users, 
  Target, 
  Heart, 
  Dumbbell,
  Layers,
  ChevronDown
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const BOOKING_URL = "https://link.sweatsetter.com/widget/booking/0bvqp14e0y6pNvAdcqsM";
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_3bc90ea7-8a21-4aef-876a-f2085af8729d/artifacts/9f97ol6g_team-situps.jpg";

const whatHappens = [
  'learn about your goals',
  'discuss your fitness background',
  'review your schedule and preferences',
  'explain your training options',
  'recommend the best next step'
];

const membershipOptions = [
  { icon: Users, title: 'Large Group Membership', desc: 'For people who want team energy, variety, and accountability.' },
  { icon: Layers, title: 'Semi-Private Coaching', desc: 'For people who want more personalized programming in a small-group setting.' },
  { icon: Target, title: 'Personal Training', desc: 'For people who want maximum individual attention.' },
  { icon: Dumbbell, title: 'HYROX Training', desc: 'For people who want performance-focused hybrid fitness and race prep.' },
  { icon: Heart, title: 'Nutrition Coaching', desc: 'For people who want stronger results outside the gym too.' },
];

const faqs = [
  {
    question: 'Do I need to be in shape before I start?',
    answer: 'No. Our programs are designed to meet you where you are. Whether you are a complete beginner or getting back into fitness after a long break, our coaches will help you scale workouts to your current ability and progress safely over time.'
  },
  {
    question: 'What should I expect at my Free Intro?',
    answer: 'Your Free Intro is a one-on-one conversation with a coach. We will talk about your goals, fitness history, schedule, and any concerns you have. Then we will recommend the best program for you to start with. There is no obligation and no pressure.'
  },
  {
    question: 'What if I\'m not sure which program is right for me?',
    answer: 'That is exactly what the Free Intro is for. After learning about your goals and preferences, we will guide you toward the best starting point, whether that is large group, semi-private, personal training, or something else.'
  },
  {
    question: 'Can beginners join?',
    answer: 'Absolutely. Many of our members started as beginners. Our coaches are trained to modify movements and scale intensity so everyone can participate safely and effectively, regardless of fitness level.'
  },
  {
    question: 'Do you offer morning and evening options?',
    answer: 'Yes. We have early morning classes starting at 5:45 AM and evening classes. Check our schedule page for the full list of available times.'
  },
  {
    question: 'Can I train for HYROX here?',
    answer: 'Yes. X-Team is an official HYROX affiliate. We offer specific HYROX training sessions and programming to help you prepare for races, whether you are a first-timer or looking to improve your time.'
  },
];

export default function Join() {
  return (
    <div data-testid="join-page">
      {/* Hero Section - Full Width Image */}
      <section 
        className="relative flex items-center justify-center"
        style={{ height: '735px', marginTop: '0' }}
        data-testid="join-hero"
        aria-label="Join hero"
      >
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url(${HERO_IMAGE})`,
            top: '135px',
            height: '600px'
          }}
        >
        </div>
        
        {/* Hero Content */}
        <div className="relative z-10 container-custom text-center" style={{ marginTop: '135px' }}>
          <div className="max-w-3xl mx-auto">
            {/* Text overlay box with semi-transparent background */}
            <div className="bg-black/30 p-[50px] inline-block">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading text-white mb-6">
                Start With a Free Intro
              </h1>
              <p className="text-lg text-white/90 mb-4">
                Meet with a coach, talk through your goals, and get a clear recommendation for the best way to begin.
              </p>
              <p className="text-white/80 mb-8">
                No pressure. No guesswork. Just a simple first step.
              </p>
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                <Button 
                  data-testid="join-hero-cta"
                  className="bg-[#FF5A3C] hover:bg-[#E64526] text-white rounded-none font-nav uppercase tracking-widest px-8 py-4 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  Book Your Free Intro
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Book Your Free Intro Headline Panel */}
      <section className="bg-[#FF5A3C] flex items-center justify-center" style={{ height: '245px' }} data-testid="booking-headline-section" aria-label="Book your free intro">
        <div className="container-custom">
          <div className="text-center">
            <h2 className="text-3xl sm:text-4xl font-heading text-white mb-2">
              Book Your Free Intro Now
            </h2>
            <p className="text-white/90">
              Choose a time that works for you.
            </p>
          </div>
        </div>
      </section>

      {/* Booking Calendar Embed */}
      <section className="section-padding bg-[#F5F7FA]" data-testid="booking-embed-section" aria-label="Booking calendar">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-none p-4 md:p-8">
              <iframe 
                src="https://link.sweatsetter.com/widget/booking/0bvqp14e0y6pNvAdcqsM" 
                className="w-full border-none rounded-none"
                style={{ minHeight: '900px' }}
                scrolling="no" 
                id="0bvqp14e0y6pNvAdcqsM_booking"
                title="Book a Free Intro"
                data-testid="gohighlevel-booking-embed"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What Happens */}
      <section className="section-padding bg-[#DCDFE3]" data-testid="what-happens-section" aria-label="What happens during your free intro">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-heading text-[#1A1A1A] mb-6">
                What Happens During Your Free Intro
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                During your Free Intro, we will:
              </p>
              <ul className="space-y-4 mb-8">
                {whatHappens.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-none bg-[#FF5A3C] text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                      {i + 1}
                    </div>
                    <span className="text-gray-700 pt-1">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-gray-600">
                If appropriate, we may also discuss personal training, semi-private training, large group training, HYROX training, nutrition coaching, or trial options.
              </p>
            </div>
            <div className="bg-white rounded-none p-8 shadow-xl">
              <h3 className="font-heading text-2xl text-[#1A1A1A] mb-6">Why This First Step Works</h3>
              <p className="text-gray-600 mb-4">
                We want your first step to feel clear and approachable.
              </p>
              <p className="text-gray-600">
                A Free Intro helps us understand where you are now, where you want to go, and what type of support will help you get there fastest.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Membership Pathways */}
      <section className="section-padding bg-white" data-testid="membership-options" aria-label="Membership options">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading text-[#1A1A1A] mb-4">
              Options Built Around Your Goals
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {membershipOptions.map((option, i) => (
              <div 
                key={i}
                className="bg-white rounded-none p-6 border border-gray-100 shadow-[0_4px_20px_rgba(0,0,0,0.05)] hover:shadow-[0_10px_30px_rgba(0,0,0,0.08)] transition-all duration-300"
                data-testid={`membership-option-${i}`}
              >
                <div className="w-12 h-12 rounded-none bg-gray-100 flex items-center justify-center mb-4">
                  <option.icon className="w-6 h-6 text-[#FF5A3C]" />
                </div>
                <h3 className="font-heading text-xl text-[#1A1A1A] mb-2">{option.title}</h3>
                <p className="text-gray-600 text-sm">{option.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reassurance */}
      <section className="section-padding bg-[#F5F7FA]" data-testid="reassurance-section">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-heading text-[#1A1A1A] mb-6">
              You Do Not Have to Figure This Out Alone
            </h2>
            <p className="text-lg text-gray-600 mb-4">
              A lot of people come to us feeling out of shape, inconsistent, intimidated, or unsure where to start.
            </p>
            <p className="text-lg text-gray-600 mb-4">
              That is normal.
            </p>
            <p className="text-lg text-gray-700 font-semibold">
              You do not need to be fit before you begin. You just need a first step.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-white" data-testid="faq-section" aria-label="Frequently asked questions">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-heading text-[#1A1A1A] mb-4">
                Frequently Asked Questions
              </h2>
            </div>
            
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, i) => (
                <AccordionItem 
                  key={i} 
                  value={`item-${i}`}
                  className="bg-[#F5F7FA] rounded-none border-none"
                  data-testid={`faq-item-${i}`}
                >
                  <AccordionTrigger className="px-6 py-4 text-left font-heading text-2xl text-[#1A1A1A] hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 text-gray-600">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding bg-gradient-to-br from-[#FF5A3C] to-[#E64526] text-white" data-testid="join-final-cta" aria-label="Call to action">
        <div className="container-custom text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading mb-6">
            Ready to Get Moving?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            We will help you find the best fit and get started with confidence.
          </p>
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
            <Button 
              data-testid="join-final-cta-btn"
              className="bg-white text-[#FF5A3C] hover:bg-gray-100 rounded-none font-nav uppercase tracking-widest px-10 py-6 text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
            >
              Book a Free Intro
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
}
